import React, { useState } from "react";
import { Alert, Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle } from "@mui/material";

/**
 * The existing template for a class, if there is one: a template made from this class (most recent wins), else
 * the template the class itself was created from. Built-in templates never count.
 */
export function findExistingTemplate(templates, course) {
  const own = (templates || []).filter((t) => !t.isSystem);
  const madeFromClass = own.filter((t) => t.sourceClassId != null && String(t.sourceClassId) === String(course.id));
  if (madeFromClass.length > 0) return madeFromClass.reduce((a, b) => (b.id > a.id ? b : a));
  if (course.sourceTemplateId != null) {
    return own.find((t) => String(t.id) === String(course.sourceTemplateId)) || null;
  }
  return null;
}

const errText = (e, fallback) => e?.response?.data?.message || e?.message || fallback;

/**
 * "Save class to the Course Bank". With no existing template it just saves a new one; with one it asks whether to
 * update that template or create a separate new template.
 *
 *   const { save, dialog, saving } = useSaveClassToBank({ tenantId, api, onDone, onError });
 *   <button onClick={() => save(course)} />   {dialog}
 *
 * `api` is { fetchClassTemplates, createClassTemplateFromClass, syncClassTemplateFromClass } so the host page
 * keeps control of which API module it uses.
 */
export function useSaveClassToBank({ tenantId, api, onDone, onError }) {
  const [pending, setPending] = useState(null); // { course, existing }
  const [saving, setSaving] = useState(false);
  const [dialogBusy, setDialogBusy] = useState(false);
  const [dialogError, setDialogError] = useState(null);

  const createNew = async (course) => {
    await api.createClassTemplateFromClass(tenantId, course.id);
    onDone?.("Saved to the Course Bank as a new template.");
  };

  const save = async (course) => {
    setSaving(true);
    try {
      let existing;
      try {
        existing = findExistingTemplate(await api.fetchClassTemplates(tenantId), course);
      } catch (e) {
        onError?.(errText(e, "Couldn't check for an existing template."));
        return;
      }
      if (existing) {
        setDialogError(null);
        setPending({ course, existing });
        return;
      }
      try {
        await createNew(course);
      } catch (e) {
        onError?.(errText(e, "Failed to save template."));
      }
    } finally {
      setSaving(false);
    }
  };

  const choose = async (mode) => {
    setDialogBusy(true);
    setDialogError(null);
    try {
      if (mode === "UPDATE") {
        await api.syncClassTemplateFromClass(tenantId, pending.existing.id, pending.course.id);
        onDone?.("Updated the existing template from this class.");
      } else {
        await createNew(pending.course);
      }
      setPending(null);
    } catch (e) {
      setDialogError(errText(e, "Failed to save template."));
    } finally {
      setDialogBusy(false);
    }
  };

  const existing = pending?.existing;
  const dialog = (
    <Dialog open={!!pending} onClose={dialogBusy ? undefined : () => setPending(null)} maxWidth="xs" fullWidth>
      <DialogTitle>This class already has a template</DialogTitle>
      <DialogContent>
        <DialogContentText component="div">
          <b>“{existing?.title}”</b> ({existing?.status === "DRAFT" ? "Draft" : "Published"}, version {existing?.contentVersion ?? 1}) was
          saved from this class. Update it with the class's current sessions and content, or save a separate new template
          and leave that one as it is.
        </DialogContentText>
        {dialogError && <Alert severity="error" sx={{ mt: 2 }}>{dialogError}</Alert>}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, flexWrap: "wrap", gap: 1 }}>
        <Button onClick={() => setPending(null)} disabled={dialogBusy}>Cancel</Button>
        <Button variant="outlined" onClick={() => choose("NEW")} disabled={dialogBusy}>Create new template</Button>
        <Button variant="contained" onClick={() => choose("UPDATE")} disabled={dialogBusy}>Update existing template</Button>
      </DialogActions>
    </Dialog>
  );

  return { save, dialog, saving };
}
